import { useEffect, useRef, useState } from "react"
import Api from "../../api/api"
import { Product } from "../../api/models"
import Spinner from "./Spinner"
import { buildImageSrcSet } from "./imageSrcSet"

const SEARCH_DEBOUNCE_MS = 300
const SEARCH_MIN_CHARS = 2

// image-less products get a square picture-icon placeholder so every chip and
// result row keeps the same shape (CardImage's pattern; chip 18px, rows 24px)
function productImage(product: Product, size: number) {
  if (product.imageUrl) {
    return (
      <img
        src={product.imageUrl}
        srcSet={buildImageSrcSet(product.imageUrl, product.availableWidths)}
        sizes={`${size}px`}
        alt=""
        style={{ width: size, height: size, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
      />
    )
  }
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: 4, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "rgba(255, 255, 255, 0.08)"
      }}
    >
      <svg width={Math.round(size * 2 / 3)} height={Math.round(size * 2 / 3)} viewBox="0 0 24 24" fill="var(--nfa-middle-grey)">
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
      </svg>
    </span>
  )
}

// Debounced product typeahead over GET /admin/products (a Developer's listing
// is server-scoped to their own products). Collapses into a dismissible chip
// while a product is selected. UserFilter's pattern.
export default function ProductFilter({ selected, onSelect, accessToken, placeholder = "Search product by name…", disabledIds, disabledHint, clearable = true, needsCredits = false, ownerId, warning }: {
  selected: Product | null
  onSelect: (product: Product | null) => void
  accessToken: string
  placeholder?: string
  // products still listed in the results but greyed out and unselectable;
  // disabledHint replaces their tooltip to say why
  disabledIds?: string[]
  disabledHint?: string
  // false = the selected chip is inert (no ✕, no click-to-clear) — for rows
  // that have their own remove control
  clearable?: boolean
  // true = search only pay-as-you-go products (server-side, so matches
  // beyond the result page aren't lost) — for the credits/topup pickers
  needsCredits?: boolean
  // narrow the search to one owner's products (server-side; admins default
  // to ALL owners without it, developers are forced to self either way)
  ownerId?: string
  // set = flag the selected chip with a warning emoji; the string is its
  // tooltip (e.g. "another owner's product")
  warning?: string
}) {
  const [text, setText] = useState("")
  const [results, setResults] = useState<Product[] | undefined>(undefined)
  const [searching, setSearching] = useState(false)
  const requestSeq = useRef(0)

  useEffect(() => {
    const query = text.trim()
    if (query.length < SEARCH_MIN_CHARS) {
      requestSeq.current++
      setResults(undefined)
      setSearching(false)
      return
    }
    const seq = ++requestSeq.current
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const page = await Api.getProducts({ accessToken, search: query, ownerId, needsCredits: needsCredits || undefined, count: 10 })
        if (seq !== requestSeq.current) return
        setResults(page.products)
      } catch {
        if (seq === requestSeq.current) setResults([])
      } finally {
        if (seq === requestSeq.current) setSearching(false)
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text, accessToken, needsCredits, ownerId])

  if (selected) {
    const chipImage = productImage(selected, 18)
    // marginLeft auto pins the mark to the chip's right edge (the chip is a
    // flex row, and full-width inside the wizard's claim rows)
    const warningMark = warning !== undefined && (
      <span role="img" aria-label="warning" title={warning} style={{ cursor: "help", marginLeft: "auto" }}>⚠️</span>
    )
    if (!clearable) {
      return (
        <span
          className="skus-filter-chip skus-filter-chip-active icon-chip"
          title={selected.id}
          style={{ cursor: "default" }}
        >
          {chipImage}
          {selected.name}
          {warningMark}
        </span>
      )
    }
    return (
      <button
        className="skus-filter-chip skus-filter-chip-active icon-chip"
        onClick={() => onSelect(null)}
        title={selected.id}
      >
        {chipImage}
        {selected.name}
        {warningMark} ✕
      </button>
    )
  }

  return (
    <div className="typeahead">
      <input
        className="filter-input"
        type="text"
        autoComplete="product-search"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {text.trim().length >= SEARCH_MIN_CHARS && (
        <div className="typeahead-results">
          {results === undefined || (searching && results.length === 0) ? (
            <div className="typeahead-empty"><Spinner size={12} /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="typeahead-empty">No matching products</div>
          ) : (
            results.map((product) => {
              const disabled = disabledIds?.includes(product.id) ?? false
              return (
              // onMouseDown so selection wins over the input losing focus
              <button
                key={product.id}
                className="typeahead-result"
                title={disabled ? disabledHint : product.id}
                disabled={disabled}
                onMouseDown={() => {
                  onSelect(product)
                  setText("")
                  setResults(undefined)
                }}
              >
                {productImage(product, 24)}
                <span className="typeahead-result-text">{product.name}</span>
              </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
